/**
 * @namespace AsyncTree
 */
/**
 * 使用异步树的节点：数据权限 节点， 使用权限页签-授权规则(选择新增规则，资源实体选'客户基本信息')
 * 
 * 异步树使用了同步树API    hideIcon
 */

import { isArray, isString, isFunction } from '@platform/api';
import useTree from '../store';
import Tree from '../Tree';
import * as TreeUtils from '../util';

const existenceCheck = function (id) {
    let store = this.asyncTreeDataSource[id];
    if (!store) {
        store = useTree();
        this.asyncTreeDataSource[id] = store;
        console.warn(`所操作的表格中无ID为${id}的数据`);
        return;
    }
    return store;
};

/**
 * 创建树
 * @memberof AsyncTree
 * @param {Config} config  -配置项信息
 * @returns {TreeComponent}
 */
export function createAsyncTree(config = {}) {
    let newConfig = Object.assign({}, config);
    let { 
        treeId, 
        expandWhenDoubleClick=true, 
        editType, 
        doubleClickEve,
        showModal=true,
        metaId,
        ...others 
    } = newConfig;
    // this.register("treeTable"); // 生成 this.treeTableDataSource[id]
    let pageScope = this,
        store = pageScope.asyncTreeDataSource[treeId];
    if (!store) {
        store = useTree();
        pageScope.asyncTreeDataSource[treeId] = store;
    }
    if (!pageScope.formDataSource[metaId] && showModal) {
        pageScope.use.form(metaId);
    }

    return <Tree
        meta={this.state.meta}
        store={store}
        treeId={treeId}
        needSearch={false}
        expandWhenDoubleClick={expandWhenDoubleClick}
        onDoubleClickEve={doubleClickEve}
        needEdit={editType}
        {...others}
        showModal={showModal}
        metaId={metaId}
        formStore={pageScope.formDataSource[metaId]}
        pageOutput={pageScope.output}
        treeType={'asyncTree'}
        _this={this}
    />;
}

/**
 * 生成树 需要的数据结构
 * @memberof AsyncTree
 * @param {Array} data - 树的数据
 * @returns {Array}
 */
export function createTreeData(data) {
    return TreeUtils.createTreeData(data);
}

/**
 * 设置树的数据
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 * @param {Array} newData - 树的数据
 * @param {Node} treeNode - 根节点
 * @param {string} searchValue - 搜索值
 */
export function setTreeData(id, newData, treeNode, searchValue = "") {
    if(!isArray(newData)){
        return;
    }
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
   
    if (!treeNode) {
        //判断是否是根节点; 不是根节点要去找对应的父节点插入child
        let treeData = createNewData(newData);
        store.setStore('treeData', treeData);
        store.setStore('selectedKeys', []); // 清空之前的选择项
        store.setCache('deepData', [...treeData]);
    } else {
        let key = '';
        if(treeNode.props && treeNode.props.eventKey){
            key = treeNode.props.eventKey;
        }else if(treeNode.key){
            key = treeNode.key;
        }
        if(key){
            let data = createNewData(newData);
            let treeData = store.getStore('treeData');
            getNewTreeData(treeData, key, data);
            store.setStore('treeData', [...treeData]);
            store.setCache('deepData', [...treeData]);
        }else{
            console.warn('异步树setTreeData方法，treeNode参数不正确');
            return;
        }
    }

    if(searchValue){
        let searchInput = store.getStore('searchInputRef');
        if (searchInput?.current && searchInput?.current?.value) {
            searchInput.current.value = searchValue;
        }
        store.setStore('searchValue', searchValue);
    }

    store.setStateEve();
    let onSelectedChange = store.getListener('onSelectedChange');
    if(isFunction(onSelectedChange && !treeNode)){
        onSelectedChange(null);
    }
}

/**
 * 设置节点勾选
 * @memberof AsyncTree
 * @param {string} treeId -树Id 
 * @param {Array | string} pk - 选中节点pk
 */
export function setNodeChecked(id, pk) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    store.setNodeChecked(pk);
}

/**
 * 获取所有被勾选的数据 pks
 * @memberof AsyncTree
 * @param {string} treeId -树Id 
 * @returns {Array}
 */
export function getCheckedKeys(id, reCalculate = true) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    if(reCalculate){
        let checkedKeys = store.getStore("checkedKeys");
        let treeData = store.getStore("treeData");
        let loopedMap = {};
        let checkAttr = [];
        const loopCheck = data => {
            for(let node of data){
                if(!loopedMap[node.refpk]){
                    checkAttr.push(node.refpk);
                    loopedMap[node.refpk] = true;
                    if(Array.isArray(node.children)){
                        loopCheck(node.children);
                    }
                }
            }
        };
        const loop = data => {
            for(let node of data){
                if(loopedMap[node.refpk]){
                    continue;
                }
                loopedMap[node.refpk] = true;
                for(let checkPk of checkedKeys){
                    if(node.refpk === checkPk){
                        checkAttr.push(checkPk);
                        if(Array.isArray(node.children)){
                            loopCheck(node.children);
                        }
                        break;
                    }
                }
                if(Array.isArray(node.children)){
                    loop(node.children);
                }
            }
        };
        loop(treeData);
        checkAttr = [...new Set(checkAttr)];
        store.setStore('checkedKeys', checkAttr);
        return checkAttr;
    }else{
        return store.getStore('checkedKeys');
    }
}

/**
 * 添加树节点
 * @memberof AsyncTree
 * @param {string} treeId - 树Id 
 * @param {Array | Item} datas - 新增树节点数据
 */
export function addTreeData(id, datas) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    let data = createNewData(datas);
    let treeData = store.getStore('treeData');
    if (isArray(data)) {
        TreeUtils.addIconBox(data);
        data.forEach(item => addTreeNode(treeData, item));
    } else {
        TreeUtils.addIconBox([data]);
        addTreeNode(treeData, data);
    }
    store.setStore('treeData', [...treeData]);
    store.setStateEve();
}

/**
 * 获取树某个节点或整个树信息
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 * @param {string} pk - 节点pk
 * @returns {Array | Item}
 */
export function getAsyncTreeValue(id, pk) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    let treeData = store.getStore('treeData');
    return TreeUtils.getItem(treeData, pk);
}

/**
 * 删除树节点
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 * @param {string} pk - 节点pk
 */
export function delTreeData(id, pk) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    store.delNodeSuceess(pk);
}

/**
 * 编辑树节点
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 * @param {Item} newItem - 节点信息
 */
export function editTreeData(id, newItem) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    store.editNodeSuccess(newItem);
}

/**
 * 设置节点不可用
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 * @param {boolean} disable - 禁用 true-禁用，false-不禁用
 * @param {Array | string} pk - 节点pk
 */
export function setAsyncNodeDisable(id, disable, pk) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }

    let treeData = store.getStore('treeData'); 
    //let deepData = store.getCache('deepData'); 

    TreeUtils.setNodeDisableEve(treeData, disable, pk);
    // // 同步deepData禁用状态
    // if (deepData) {
    //     TreeUtil.setNodeDisableEve(deepData, disable, pk);
    // }
    // 如果是整体禁用模式，则禁用快捷键功能
    if (!pk) {
        if (disable) {
            // 全被禁用
            store.setStore('focusable', false);
        } else {
            store.setStore('focusable', true);
        }
    }
    store.setStore('treeData', [...treeData]);
    store.setStateEve();
}

/**
 * 设置树的编辑性
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 * @param {boolean} editable - 可用性 
 */
export function setTreeEdit(id, status) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    let needEdit = store.getStore('needEdit');
    if (needEdit === null) {
        needEdit = true;
    }
    if (needEdit !== status) {
        let onTreeStatusChange = store.getListener('onTreeStatusChange');
        if (onTreeStatusChange) {
            onTreeStatusChange();
        }
    }
    store.setStore('needEdit', status); 
    store.setStateEve();
}

/**
 * 获取当前选中节点信息
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 * @returns {Item}
 */
export function getSelectNodeAsync(id) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    return store.getCache('selectedNode'); 
}

/**
 * 展开节点方法
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 * @param {Array | string} pk - 展开节点pk
 * @param {boolean} reset - 重置展开节点
 * @param {boolean} autoExpandParent -自动展开父节点
 */
export function openNodeByPkAsync(id, pk, reset, autoExpandParent = true, needLoadData = true) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    let expandedKeys = store.getStore('expandedKeys');

    if (reset) {
        expandedKeys = Object.prototype.toString.call(pk) === '[object Array]' ? pk : (pk ? [pk] : []);
    } else if (Object.prototype.toString.call(pk) === '[object Array]') {
        expandedKeys.push(...pk);
    } else if (Object.prototype.toString.call(pk) === '[object String]') {
        if (expandedKeys.indexOf(pk) === -1) {
            expandedKeys.push(pk);
        }
    } else {
        console.warn("openNodeByPk方法，参数pk,类型为数组或字符串");
        return;
    }

    store.setStore('autoExpandParent', autoExpandParent); 
    store.setStore('expandedKeys', [...expandedKeys]); 
    store.setStateEve();

    let loadTreeData = store.getListener('loadTreeData');
    let treeData = store.getStore('treeData');
    if (isFunction(loadTreeData) && needLoadData) {
        if (isString(pk)) {
            loadTreeData(pk, TreeUtils.getItem(treeData, pk));
        } else if (isArray(pk)) {
            pk.forEach(item => {
                loadTreeData(item, TreeUtils.getItem(treeData, item));
            });
        }
    }
}

/**
 * 关闭节点方法
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 * @param {Array | string} pk - 关闭节点pk
 */
export function closeNodeByPkAsync(id, pk) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    store.closeNodeByPk(pk);
}

/**
 * 设置图标显示隐藏
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 * @param {string} key - 节点pk
 * @param {Object} data - 图标数据 { editIcon:true, delIcon:false, addIcon:false }
 */
export function hideIconAsync(id, key, data) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    store.hideIcon(key, data);
}

/**
 * 新增兄弟节点
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 * @param {Item} data - 新增节点数据
 */
export function addBrotherNodeAsync(id, data) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    store.addBrotherNode(data);
}

/**
 * 设置节点选中
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 * @param {Array | string} pk - 选中节点pk
 */
export function setNodeSelectedAsync(id, pk, triggerMouseEnter = true) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    store.setNodeSelected(pk);
    let onMouseEnterEve = store.getListener('onMouseEnterEve');
    if(typeof onMouseEnterEve === 'function' && triggerMouseEnter){
        let treeData = store.getStore('treeData'),
            item = TreeUtils.getItem(treeData, pk);
        onMouseEnterEve(pk, { ...item });
    }
}

/**
 * 取消当前选中所有节点
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 */
export function cancelSelectedNodeAsync(id) {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }
    store.cancelSelectedNode();
}

/**
 * 设置搜索值
 * @memberof AsyncTree
 * @param {string} treeId - 树Id
 * @param {string} searchValue - 搜索值
 */
export function setSearchValue(id, searchValue = "") {
    let store = existenceCheck.call(this, id);
    if (!store) {
        return;
    }

    let searchInput = store.getStore('searchInputRef');
    if (searchInput?.current) {
        searchInput.current.value = searchValue;
    }
    store.setStore('searchValue', searchValue);
    store.setStateEve();
}

// 组装树组件需要的结构
function createNewData(val){
    if (isArray(val)) {
        val.map((item, index) => {
            item.key = item.refpk;
            if (!item.iconBox) {
                if (item.key) {
                    item.iconBox = {
                        addIcon: true,
                        delIcon: true,
                        editIcon: true,
                    };
                }
            }
            if (item.key === undefined) {
                console.error('createNewData方法报错，异步树的数据中缺少refpk字段，请检查数据格式');
                return false;
            }
        });
    } else if (val.refpk) {
        val.key = val.refpk;
        if (!val.iconBox) {
            val.iconBox = {
                addIcon: true,
                delIcon: true,
                editIcon: true,
            };
        }
    } else {
        console.error('createNewData, 异步树，数据格式不正确');
    }
    return val;
}

//  插入对应的 树 子节点数据
function getNewTreeData(treeData, parentKey, child) {
    if (!child || !isArray(child) || child.length === 0) {
        return false;
    }
    const loop = data => {
        if (!data || data.length === 0) {
            return false;
        }
        for(let item of data){
            if (item.key === child[0].pid || item.refpk === child[0].pid) {
                item.children = child;
                return;
            } 
            if (item.children) {
                loop(item.children);
            }
        }
    };
    loop(treeData);
    return treeData;
}

// 新增节点，插入节点
function addTreeNode(treeData, data) {
    let pid = data.pid;
    if (pid) {
        let len = treeData.length;
        for (let i = 0; i < len; i++) {
            let node = treeData[i];
            if (node.refpk === pid) {
                if (node.children) {
                    node.children.push(data);
                } else {
                    node.children = [data];
                }
                return true;
            } else if (Array.isArray(node.children)) {
                let res = addTreeNode(node.children, data);
                if (res) {
                    return false;
                }
            }
        }
    } else {
        if (data.refpk) {
            treeData.push(data);
        } else {
            console.warn('addTreeNode方法，参数data格式不正确');
        }
    }
}

